/**
 * SharePoint → `SuggestedLoad` for Load Building.
 * - Agile rows → McLeod Enterprise shape (`agileSharepointRowToSuggestedLoad`).
 * - Vektor rows → Turvo shape (`vektorSharepointRowToSuggestedLoad`).
 * Forms apply via existing `clickedSuggestion` merge in `UnifiedMcLeodLoadForm` / `TurvoLoadForm` — not Aljex.
 */
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { searchCustomers } from 'lib/api/searchCustomers';
import { searchLocations } from 'lib/api/searchLocations';
import {
  type AdditionalReference,
  type CompanyCoreInfo,
  type NormalizedLoad,
  type TMSCustomer,
  type TMSLocation,
  createInitLoad,
  normalizeLoad,
} from 'types/Load';
import type { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';
import {
  SharepointLoadBuildingSources,
  TMS,
  TMSTimezoneConfig,
} from 'types/enums/Integrations';
import {
  SuggestionCategories,
  SuggestionPipelines,
} from 'types/suggestions/CoreSuggestions';
import type {
  AppliedLoad,
  LoadBuildingSuggestions,
  SuggestedLoad,
} from 'types/suggestions/LoadBuildingSuggestions';
import { SuggestionStatus } from 'types/suggestions/LoadSuggestions';
import { SuggestionSourceCategories } from 'types/suggestions/QuoteSuggestions';
import { normalizeUTCDate } from 'utils/time';

import {
  type SharepointAgileRow,
  type SharepointRow,
} from '../types/Sharepoint';
import {
  SHAREPOINT_AGILE_RATE_DEFAULTS,
  SHAREPOINT_AGILE_SPECIFICATIONS_DEFAULTS,
  SHAREPOINT_CONSIGNEE,
  SHAREPOINT_CUSTOMER,
  SHAREPOINT_PICKUP,
} from './sharepointLoadBuildingConstants';

dayjs.extend(utc);
dayjs.extend(timezone);

const SHAREPOINT_APPOINTMENT_TIMEZONE = SHAREPOINT_PICKUP.timezone;

function firstTrimmedNonEmpty(...values: MaybeUndef<string>[]): Undef<string> {
  for (const v of values) {
    const t = v?.trim();
    if (t) return t;
  }
  return undefined;
}

function buildDateInTimezone(
  year: number,
  monthZeroBased: number,
  day: number,
  hour: number,
  minute: number,
  tz: string
): Maybe<Date> {
  const month = monthZeroBased + 1;
  const dateText = `${String(year).padStart(4, '0')}-${String(month).padStart(
    2,
    '0'
  )}-${String(day).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(
    minute
  ).padStart(2, '0')}:00`;
  const parsed = dayjs.tz(dateText, tz);
  if (!parsed.isValid()) return null;
  return parsed.toDate();
}

const AGILE_DAY_MONTH_ABBREV: Record<string, number> = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

function parseSlashOrDMonDateAt6Am(
  datePortion: string,
  defaultYear: number
): Maybe<Date> {
  const dMon = datePortion.match(/^(\d{1,2})-([A-Za-z]{3})(?:-(\d{2,4}))?$/i);
  if (dMon) {
    const day = parseInt(dMon[1], 10);
    const monKey = dMon[2].slice(0, 3).toLowerCase();
    const month = AGILE_DAY_MONTH_ABBREV[monKey];
    if (month == null || day < 1 || day > 31) return null;
    let year = defaultYear;
    if (dMon[3]) {
      let y = parseInt(dMon[3], 10);
      if (y < 100) y += 2000;
      year = y;
    }
    return buildDateInTimezone(
      year,
      month,
      day,
      6,
      0,
      SHAREPOINT_APPOINTMENT_TIMEZONE
    );
  }
  const slash = datePortion.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?$/);
  if (slash) {
    const month = parseInt(slash[1], 10) - 1;
    const day = parseInt(slash[2], 10);
    let year = defaultYear;
    if (slash[3]) {
      let y = parseInt(slash[3], 10);
      if (y < 100) y += 2000;
      year = y;
    }
    return buildDateInTimezone(
      year,
      month,
      day,
      6,
      0,
      SHAREPOINT_APPOINTMENT_TIMEZONE
    );
  }
  return null;
}

/**
 * Parses Syfan **Agile** tender sheet "load day" text (e.g. `LOAD DAY` / `In Route to Agile`)
 * into a calendar date at **06:00** in {@link SHAREPOINT_APPOINTMENT_TIMEZONE} (via
 * {@link parseSlashOrDMonDateAt6Am}).
 *
 * - Accepts `d-mon` / `d-mon-yy(yy)` (e.g. `24-Jan`, `24-Jan-26`) and `m/d` / `m/d/yy(yy)`.
 * - If the cell contains `@`, only the substring **before** `@` is parsed (extra notes after the at-sign).
 * - Placeholder values (`reload`, `n/a`, `tbd`, `pending`, case-insensitive) -> `null`.
 * - Empty / whitespace -> `null`.
 *
 * @param raw Cell value from the workbook (may include trailing `@...` metadata).
 * @param defaultYear Used when the parsed format omits a year (typically current calendar year).
 * @returns Parsed instant, or `null` if missing, placeholder, or unrecognized format.
 */
function parseAgileLoadOrRouteDayAt6Am(
  raw: MaybeUndef<string>,
  defaultYear: number
): Maybe<Date> {
  if (raw == null) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  if (
    lower === 'reload' ||
    lower === 'n/a' ||
    lower === 'tbd' ||
    lower === 'pending'
  ) {
    return null;
  }
  const at = trimmed.indexOf('@');
  const datePortion = at >= 0 ? trimmed.slice(0, at).trim() : trimmed;
  return parseSlashOrDMonDateAt6Am(datePortion, defaultYear);
}

/**
 * Load day is parsed at 6:00 in `SHAREPOINT_APPOINTMENT_TIMEZONE`; pickup (and consignee) use the next calendar day at 6:00.
 */
function nextCalendarDayAtSixAmSharepointTz(loadDayAtSixAm: Date): Date {
  const z = dayjs(loadDayAtSixAm)
    .tz(SHAREPOINT_APPOINTMENT_TIMEZONE)
    .add(1, 'day');
  return (
    buildDateInTimezone(
      z.year(),
      z.month(),
      z.date(),
      6,
      0,
      SHAREPOINT_APPOINTMENT_TIMEZONE
    ) ?? z.toDate()
  );
}

function apptDateForTms(tmsName: TMS, raw: Maybe<Date>): Maybe<Date> {
  if (raw == null) return null;
  if (TMSTimezoneConfig[tmsName].isLoadTimezoneAgnostic) {
    return normalizeUTCDate(raw);
  }
  return raw;
}

function agileSharepointRowToSuggestedLoad(
  row: SharepointAgileRow,
  tmsName: TMS
): SuggestedLoad {
  const base = normalizeLoad(tmsName, createInitLoad()) as NormalizedLoad;
  /** Customer PO only - booking is its own `additionalReferences` row so Kit can show both. */
  const poNums = row.customerPo?.trim() || '';
  const additionalReferences: AdditionalReference[] = [
    {
      qualifier: 'Booking',
      number: row.bookingNumber || '',
      weight: 0,
      pieces: 0,
      shouldSendToDriver: false,
    },
    {
      qualifier: 'Container',
      number: row.containerNumber || '',
      weight: 0,
      pieces: 0,
      shouldSendToDriver: false,
    },
  ];

  const defaultYear = new Date().getFullYear();

  /** Col B (`loadDay`) and col H (`inRouteToAgile`) both carry the same "load day" on Syfan sheets. */
  const loadDaySource = firstTrimmedNonEmpty(row.loadDay, row.inRouteToAgile);
  const loadDayAtSixAm = parseAgileLoadOrRouteDayAt6Am(
    loadDaySource,
    defaultYear
  );
  const pickupAndConsigneeAppt: Maybe<Date> =
    loadDayAtSixAm != null
      ? nextCalendarDayAtSixAmSharepointTz(loadDayAtSixAm)
      : null;

  return {
    mode: base.mode,
    moreThanTwoStops: base.moreThanTwoStops,
    poNums,
    notes: base.notes,
    customer: { ...base.customer, ...SHAREPOINT_CUSTOMER },
    stops: base.stops,
    pickup: {
      ...base.pickup,
      ...SHAREPOINT_PICKUP,
      addressLine2: base.pickup.addressLine2 ?? '',
      additionalReferences: [],
      apptStartTime: apptDateForTms(tmsName, pickupAndConsigneeAppt),
    },
    consignee: {
      ...base.consignee,
      ...SHAREPOINT_CONSIGNEE,
      addressLine2: base.consignee.addressLine2 ?? '',
      additionalReferences: [],
      apptStartTime: apptDateForTms(tmsName, pickupAndConsigneeAppt),
    },
    rateData: { ...base.rateData, ...SHAREPOINT_AGILE_RATE_DEFAULTS },
    specifications: {
      ...base.specifications,
      ...SHAREPOINT_AGILE_SPECIFICATIONS_DEFAULTS,
    },
    additionalReferences,
    commodities: base.commodities,
  };
}

/**
 * Converts an Agile EWA row to `SuggestedLoad`.
 */
export function sharepointRowToSuggestedLoad(
  row: SharepointRow
): SuggestedLoad {
  return agileSharepointRowToSuggestedLoad(row, TMS.McleodEnterprise);
}

export type MatchedSharepointDefaults = {
  customer?: TMSCustomer;
  pickup?: TMSLocation;
  consignee?: TMSLocation;
};

const COMPANY_ADDRESS_DISPLAY_FIELDS: (keyof CompanyCoreInfo)[] = [
  'name',
  'addressLine1',
  'addressLine2',
  'city',
  'state',
  'zipCode',
  'country',
];

function isBlankDisplayField(v: unknown): boolean {
  if (v == null) return true;
  if (typeof v === 'string') return v.trim() === '';
  return false;
}

function mergeCompanyCorePreferringOverlayIds<T extends CompanyCoreInfo>(
  base: T,
  overlay: T
): T {
  const merged = { ...base, ...overlay } as T;
  const mergedRecord = merged as Record<string, unknown>;
  const baseRecord = base as Record<string, unknown>;
  const overlayRecord = overlay as Record<string, unknown>;
  for (const k of COMPANY_ADDRESS_DISPLAY_FIELDS) {
    if (
      isBlankDisplayField(overlayRecord[k]) &&
      !isBlankDisplayField(baseRecord[k])
    ) {
      mergedRecord[k] = baseRecord[k];
    }
  }
  return merged;
}

async function resolveCustomer(tmsID: number): Promise<Undef<TMSCustomer>> {
  const byName = await searchCustomers(tmsID, 'name', SHAREPOINT_CUSTOMER.name);
  if (byName.isOk() && byName.value.customerList?.length > 0) {
    return byName.value.customerList[0];
  }
  const byAddress = await searchCustomers(
    tmsID,
    'addressLine1',
    SHAREPOINT_CUSTOMER.addressLine1
  );
  if (byAddress.isOk() && byAddress.value.customerList?.length > 0) {
    return byAddress.value.customerList[0];
  }
  return undefined;
}

async function resolvePickup(tmsID: number): Promise<Undef<TMSLocation>> {
  const byName = await searchLocations(
    tmsID,
    'name',
    SHAREPOINT_PICKUP.name,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined
  );
  if (byName.isOk() && byName.value.locationList?.length > 0) {
    return byName.value.locationList[0];
  }
  const byAddress = await searchLocations(
    tmsID,
    'addressLine1',
    SHAREPOINT_PICKUP.addressLine1,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined
  );
  if (byAddress.isOk() && byAddress.value.locationList?.length > 0) {
    return byAddress.value.locationList[0];
  }
  return undefined;
}

async function resolveConsignee(tmsID: number): Promise<Undef<TMSLocation>> {
  const byName = await searchLocations(
    tmsID,
    'name',
    SHAREPOINT_CONSIGNEE.name,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined
  );
  if (byName.isOk() && byName.value.locationList?.length > 0) {
    return byName.value.locationList[0];
  }
  const byAddress = await searchLocations(
    tmsID,
    'addressLine1',
    SHAREPOINT_CONSIGNEE.addressLine1,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined
  );
  if (byAddress.isOk() && byAddress.value.locationList?.length > 0) {
    return byAddress.value.locationList[0];
  }
  return undefined;
}

/**
 * Uses the same search APIs as the Load Building form to match the fixed SharePoint
 * customer, pickup, and consignee to existing TMS records. Returns matched records
 * (with externalTMSID) so suggestions can show a selected location/customer in the dropdown.
 * Customer, pickup, and consignee searches run in parallel to minimize total wait time.
 */
export async function matchSharepointDefaultsToTMS(
  tmsID: number
): Promise<MatchedSharepointDefaults> {
  const [customer, pickup, consignee] = await Promise.all([
    resolveCustomer(tmsID),
    resolvePickup(tmsID),
    resolveConsignee(tmsID),
  ]);
  const result: MatchedSharepointDefaults = {};
  if (customer != null) result.customer = customer;
  if (pickup != null) result.pickup = pickup;
  if (consignee != null) result.consignee = consignee;
  return result;
}

export async function matchSharepointRowToTMS(
  tmsID: number,
  _row: SharepointRow
): Promise<MatchedSharepointDefaults> {
  return matchSharepointDefaultsToTMS(tmsID);
}

/**
 * Overlay TMS search hits onto the synthetic SharePoint suggestion while keeping
 * sheet-derived appointments and reference data.
 */
export function mergeSharepointSuggestedWithTMSMatches(
  suggested: SuggestedLoad,
  matched: MatchedSharepointDefaults
): SuggestedLoad {
  return {
    ...suggested,
    customer: matched.customer
      ? mergeCompanyCorePreferringOverlayIds(
          suggested.customer,
          matched.customer
        )
      : suggested.customer,
    // TMS search hits are `TMSLocation` (no appointment fields); keep sheet-derived appts.
    pickup: matched.pickup
      ? ({
          ...mergeCompanyCorePreferringOverlayIds(
            suggested.pickup,
            matched.pickup as CompanyCoreInfo
          ),
          apptStartTime: suggested.pickup?.apptStartTime ?? null,
          apptEndTime: suggested.pickup?.apptEndTime ?? null,
          readyTime: suggested.pickup?.readyTime ?? null,
        } as SuggestedLoad['pickup'])
      : suggested.pickup,
    consignee: matched.consignee
      ? ({
          ...mergeCompanyCorePreferringOverlayIds(
            suggested.consignee,
            matched.consignee as CompanyCoreInfo
          ),
          apptStartTime: suggested.consignee?.apptStartTime ?? null,
          apptEndTime: suggested.consignee?.apptEndTime ?? null,
          mustDeliver: suggested.consignee?.mustDeliver ?? null,
        } as SuggestedLoad['consignee'])
      : suggested.consignee,
  };
}

/**
 * Deterministic negative id so SharePoint-derived Kit LB suggestions keep the same `id`
 * across sheet refetches. That keeps Kit selection / dismiss matching stable (time-based
 * ids broke "go to next load" after each sync).
 */
export function stableSyntheticSuggestionIdForSharepointRow(
  row: SharepointRow
): number {
  const key = `agile|${row.index}|${row.bookingNumber?.trim() ?? ''}|${row.customerPo?.trim() ?? ''}|${row.containerNumber?.trim() ?? ''}`;
  let h = 2166136261;
  for (let i = 0; i < key.length; i += 1) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  h |= 0;
  if (h === 0 || h === -2147483648) {
    return -201001;
  }
  return h < 0 ? h : -Math.abs(h);
}

export function sharepointSourceExternalIDForRow(
  row: SharepointRow,
  worksheet: string
): string {
  const ws = worksheet.trim();
  const booking = row.bookingNumber?.trim() ?? '';
  return `agile|${ws}|${booking}`;
}

export function sharepointLoadBuildingSourceForRow(
  _row: SharepointRow
): SharepointLoadBuildingSources {
  return SharepointLoadBuildingSources.Agile;
}

export type SharepointLoadBuildingPersistRequest = {
  sourceCategory: SuggestionSourceCategories.Sharepoint;
  source: SharepointLoadBuildingSources;
  sourceExternalID: string;
  sourceURL: string;
  suggested: SuggestedLoad;
};

export function sharepointPersistRequestForRow(
  row: SharepointRow,
  suggested: SuggestedLoad,
  options: { worksheet: string; sourceURL: string }
): SharepointLoadBuildingPersistRequest {
  return {
    sourceCategory: SuggestionSourceCategories.Sharepoint,
    source: sharepointLoadBuildingSourceForRow(row),
    sourceExternalID: sharepointSourceExternalIDForRow(row, options.worksheet),
    sourceURL: options.sourceURL,
    suggested,
  };
}

/**
 * Same object shape as backend load-building suggestions so Load Building can use
 * `clickedSuggestion` exactly like Kit "apply suggestion" (`handleApplySuggestion`).
 * Use a unique negative `id` so it never collides with real suggestion IDs.
 */
export function buildSyntheticSharepointLoadBuildingSuggestion(
  suggested: SuggestedLoad,
  emailID: number,
  options?: {
    syntheticSuggestionId?: number;
    source?: string;
    sourceExternalID?: string;
    sourceURL?: string;
    sourceCategory?: SuggestionSourceCategories;
  }
): LoadBuildingSuggestions {
  const id = options?.syntheticSuggestionId ?? -Date.now();
  return {
    id,
    createdAt: new Date().toISOString(),
    account: '',
    status: SuggestionStatus.Pending,
    emailID,
    latestEmailIDFromThread: emailID,
    pipeline: SuggestionPipelines.LoadBuilding,
    category: SuggestionCategories.LoadBuilding,
    attachment: null,
    source: options?.source,
    sourceExternalID: options?.sourceExternalID,
    sourceURL: options?.sourceURL,
    sourceCategory: options?.sourceCategory,
    suggested,
    applied: { ...suggested } as AppliedLoad,
  };
}
